/**
 * Sub-rogar um título e enviá-lo para o jurídico (AF-3674).
 *
 * ⚠️ As rotas são do SETTLEMENT, não do billing, embora o botão viva na tela de Títulos (que é do
 * billing): as flags são colunas de `settlement_v2.title_debt`, e quem escreve é o dono do dado.
 * A tela de Títulos só as LÊ, pela `vw_charges_list`.
 *
 * Fica neste arquivo — e não no `settlementApi` — porque quem chama é a tela de billing; separar
 * evita que a tela de Títulos importe o módulo inteiro de settlement por duas funções.
 */
import { Auth } from 'aws-amplify';
import appConfig from '../../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.settlementV2;

/** Os dois atos. O valor é o que a rota do settlement espera no caminho. */
export type TitleDebtFlag = 'SUBROGATION' | 'JUDICIAL_PETITION';

/**
 * O que fazer com as faturas abertas do título. **Obrigatório**: o servidor recusa sem isso, de
 * propósito — cancelar é a mais destrutiva das duas e não pode virar default.
 */
export type OpenInvoicesAction = 'CANCEL' | 'UNCOLLECT';

const PATHS: Record<TitleDebtFlag, string> = {
    SUBROGATION: 'subrogation',
    JUDICIAL_PETITION: 'judicial-petition',
};

const getHeaders = async () => {
    const session = await Auth.currentSession();
    return {
        'x-api-key': API_KEY,
        'Authorization': `Bearer ${session.getIdToken().getJwtToken()}`,
        'Content-Type': 'application/json',
    };
};

/**
 * ⚠️ `openInvoicesAction` é OPCIONAL porque nem todo ato pergunta: o envio para o jurídico sempre
 * para de cobrar, e quem define isso é o SERVIDOR. Mandar daqui deixaria outro cliente livre para
 * pedir o contrário — a regra é do domínio.
 */
export const flagTitleDebt = async (
    titleDebtRef: string, flag: TitleDebtFlag, openInvoicesAction?: OpenInvoicesAction,
) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${titleDebtRef}/${PATHS[flag]}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(
            openInvoicesAction ? { open_invoices_action: openInvoicesAction } : {}),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};
